import React, { useState, useEffect } from 'react';
import { LogIn, User, Lock, Eye, EyeOff, Users } from 'lucide-react';
import RedirectModal from './RedirectModal';
import ChangePasswordModal from './ChangePasswordModal';
import { authService } from './authService';
import { getApiUrl, getCurrentRegion } from './utils/apiConfig';

interface LoginProps {
  onLogin: (username: string, password: string) => Promise<void>;
}

interface RedirectInfo {
  show: boolean;
  role: string;
  redirectUrl: string;
  userName: string;
}

type Step = 'login' | 'otp' | 'contact' | 'poste';

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [redirectInfo, setRedirectInfo] = useState<RedirectInfo>({
    show: false,
    role: '',
    redirectUrl: '',
    userName: ''
  });

  // Etat OTP / appareil de confiance
  const [step, setStep] = useState<Step>('login');
  const [challengeId, setChallengeId] = useState('');
  const [otpChannel, setOtpChannel] = useState('');
  const [otpDestination, setOtpDestination] = useState('');
  const [pendingRef, setPendingRef] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [emailInput, setEmailInput] = useState('');
  // Verrou poste : code d'enrolement remis par le superviseur
  const [codeEnrolement, setCodeEnrolement] = useState('');

  // Changement de mot de passe obligatoire AVANT la redirection vers le module (comptes agents/superviseurs
  // rediriges en 403 : sans cela le mot de passe provisoire restait en service, constate le 27/09/2026)
  const [redirectApresMdp, setRedirectApresMdp] = useState<RedirectInfo | null>(null);

  const dtKey = () => 'conquete_dt_' + username;

  // Effectuer la redirection après le countdown
  useEffect(() => {
    if (redirectInfo.show && redirectInfo.redirectUrl) {
      const timer = setTimeout(() => {
        window.location.href = redirectInfo.redirectUrl;
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [redirectInfo]);

  // Envoie la requete de login (avec le token d'appareil si connu) et gere la reponse
  const doLogin = async () => {
    setError(null);
    setIsLoading(true);
    try {
      const deviceToken = localStorage.getItem(dtKey()) || '';
      const response = await fetch(getApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(deviceToken ? { 'X-Device-Token': deviceToken } : {})
        },
        body: JSON.stringify({ username, password, ...(codeEnrolement ? { codeEnrolement } : {}) }),
      });

      const data = await response.json();

      // Verrou poste : jeton du poste fraichement enrole -> memoriser pour cet identifiant
      if (data.deviceToken) {
        localStorage.setItem(dtKey(), data.deviceToken);
      }

      // Etape OTP : code a saisir
      if (data.step === 'otp_required') {
        setChallengeId(data.challengeId);
        setOtpChannel(data.canal);
        setOtpDestination(data.destination || '');
        setOtpCode('');
        setStep('otp');
        setIsLoading(false);
        return;
      }

      // Etape capture d'email (aucun contact verifie)
      if (data.step === 'contact_required') {
        setPendingRef(data.pendingRef);
        setEmailInput('');
        setStep('contact');
        setIsLoading(false);
        return;
      }

      // Verrou poste : machine non autorisee pour cet agent -> demander le code d'enrolement
      if (data.step === 'poste_non_autorise') {
        setStep('poste');
        // Si un code venait d'etre soumis et refuse -> message explicite
        if (codeEnrolement) {
          setError(data.reason === 'invalide_ou_expire'
            ? "Code invalide ou expire. Demandez-en un nouveau a votre superviseur."
            : (data.message || 'Poste non autorise.'));
        }
        setIsLoading(false);
        return;
      }

      // Cas special: Agent doit etre redirige
      if (response.status === 403 && data.redirectUrl) {
        if (data.token && data.user) {
          authService.saveCredentials(data.token, data.user, data.requirePasswordChange);
        }
        const infoRedirection = {
          show: true,
          role: data.role,
          redirectUrl: data.redirectUrl,
          userName: data.user?.nom_complet || username
        };
        if (data.requirePasswordChange && data.token) {
          setRedirectApresMdp(infoRedirection);
        } else {
          setRedirectInfo(infoRedirection);
        }
        setIsLoading(false);
        return;
      }

      // Connexion normale via onLogin
      if (response.ok && data.success) {
        await onLogin(username, password);
      } else {
        throw new Error(data.message || 'Erreur de connexion');
      }
    } catch (err: any) {
      if (!redirectInfo.show) {
        setError(err.message || 'Erreur de connexion');
      }
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await doLogin();
  };

  // Verifie le code OTP -> enrole l'appareil -> relance le login (appareil desormais de confiance)
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const response = await fetch(getApiUrl('/api/auth/verify-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challengeId, code: otpCode }),
      });
      const data = await response.json();
      if (!data.verified || !data.deviceToken) {
        throw new Error(data.message || 'Code invalide ou expire.');
      }
      // Memoriser le token d'appareil pour cet identifiant, puis finaliser via /login
      localStorage.setItem(dtKey(), data.deviceToken);
      setStep('login');
      await doLogin();
    } catch (err: any) {
      setError(err.message || 'Code invalide.');
      setIsLoading(false);
    }
  };

  // Enregistre l'email et demande l'envoi du code de verification
  const handleContact = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const response = await fetch(getApiUrl('/api/auth/contact'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pendingRef, email: emailInput }),
      });
      const data = await response.json();
      if (data.step !== 'otp_required') {
        throw new Error(data.message || 'Impossible d\'envoyer le code.');
      }
      setChallengeId(data.challengeId);
      setOtpChannel(data.canal);
      setOtpDestination(data.destination || '');
      setOtpCode('');
      setStep('otp');
      setIsLoading(false);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'envoi du code.');
      setIsLoading(false);
    }
  };

  // Verrou poste : soumet le code d'enrolement en relancant le login (le code part dans le body)
  const handlePosteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await doLogin();
  };

  const backToLogin = () => {
    setStep('login');
    setError(null);
    setIsLoading(false);
    setCodeEnrolement('');
  };

  const inputBase = 'block w-full pl-10 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500';
  const primaryBtn = 'w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

  // Hote vote.conquete.app = campagne "Vote meilleure commune" (Bouake) : titre et onglet dedies
  const estHoteVote = window.location.hostname.toLowerCase().startsWith('vote.');
  useEffect(() => {
    if (estHoteVote) document.title = 'Vote meilleure commune - Bouaké';
  }, [estHoteVote]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
              {step === 'login' ? <Users className="w-8 h-8 text-orange-600" /> : <Lock className="w-8 h-8 text-orange-600" />}
            </div>
            {/* Campagne Vote meilleure commune (docs/CDC-JEU-COMMUNE.md R25) : sur vote.conquete.app, le portail
                s'affiche au nom de la campagne ; ailleurs, titre habituel */}
            {estHoteVote ? (
              <>
                <h1 className="text-2xl font-bold text-gray-800">Vote meilleure commune</h1>
                <p className="text-orange-600 font-medium mt-1">Bouaké - Élection de la meilleure commune de Côte d'Ivoire</p>
              </>
            ) : (
              <h1 className="text-2xl font-bold text-gray-800">Conquête {getCurrentRegion()?.label.split(' (')[0] || 'Multi-Région'} 2025</h1>
            )}
            <p className="text-gray-600 mt-2">
              {step === 'login' && (estHoteVote ? 'Connectez-vous pour accéder aux appels' : 'Connectez-vous pour accéder au système')}
              {step === 'otp' && 'Vérification de sécurité'}
              {step === 'contact' && 'Vérification de votre email'}
              {step === 'poste' && 'Autorisation du poste'}
            </p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {step === 'login' && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">Identifiant</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text" id="username" value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className={inputBase} placeholder="Votre identifiant" required disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">Mot de passe</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'} id="password" value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputBase + ' pr-10'} placeholder="••••••••" required disabled={isLoading}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    title={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}>
                    {showPassword ? <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" /> : <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={isLoading} className={primaryBtn}>
                {isLoading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> : <><LogIn className="w-5 h-5 mr-2" />Se connecter</>}
              </button>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={handleVerifyOtp} className="space-y-6">
              <p className="text-sm text-gray-600">
                Un code de vérification a été envoyé {otpChannel === 'sms' ? 'par SMS au' : 'à'} <span className="font-medium">{otpDestination}</span>. Saisissez-le pour valider cet appareil.
              </p>
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">Code à 6 chiffres</label>
                <input
                  type="text" id="otp" inputMode="numeric" maxLength={6} value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  className="block w-full px-3 py-2 text-center tracking-[0.5em] text-lg border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="------" required disabled={isLoading} autoFocus
                />
              </div>
              <button type="submit" disabled={isLoading || otpCode.length < 6} className={primaryBtn}>
                {isLoading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> : <><Lock className="w-5 h-5 mr-2" />Valider</>}
              </button>
              <button type="button" onClick={backToLogin} className="w-full flex justify-center items-center text-sm text-gray-500 hover:text-gray-700">
                Retour
              </button>
            </form>
          )}

          {step === 'contact' && (
            <form onSubmit={handleContact} className="space-y-6">
              <p className="text-sm text-gray-600">
                Pour sécuriser votre compte, renseignez votre adresse email. Un code de vérification vous y sera envoyé.
              </p>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Adresse email</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email" id="email" value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    className={inputBase} placeholder="vous@exemple.com" required disabled={isLoading} autoFocus
                  />
                </div>
              </div>
              <button type="submit" disabled={isLoading} className={primaryBtn}>
                {isLoading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> : <><User className="w-5 h-5 mr-2" />Envoyer le code</>}
              </button>
              <button type="button" onClick={backToLogin} className="w-full flex justify-center items-center text-sm text-gray-500 hover:text-gray-700">
                Retour
              </button>
            </form>
          )}

          {step === 'poste' && (
            <form onSubmit={handlePosteSubmit} className="space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                Cet appareil n'est pas encore autorisé pour votre compte. Pour l'activer une seule fois,
                saisissez le code d'enrôlement remis par votre superviseur lors de la formation. Sinon,
                connectez-vous depuis votre poste habituel.
              </div>
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">Code d'enrôlement</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text" id="code" value={codeEnrolement}
                    onChange={(e) => setCodeEnrolement(e.target.value.toUpperCase())}
                    className={inputBase + ' tracking-widest uppercase'} placeholder="XXXXX-XXXXX"
                    required disabled={isLoading} autoFocus autoComplete="off"
                  />
                </div>
              </div>
              <button type="submit" disabled={isLoading || codeEnrolement.replace(/[^A-Z0-9]/g, '').length < 10} className={primaryBtn}>
                {isLoading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> : <><Lock className="w-5 h-5 mr-2" />Activer ce poste</>}
              </button>
              <button type="button" onClick={backToLogin} className="w-full flex justify-center items-center text-sm text-gray-500 hover:text-gray-700">
                Retour
              </button>
            </form>
          )}

          <div className="mt-6 text-center text-sm text-gray-600">
            <p>
              powered by <span className="font-bold">RED<span className="text-red-600">LINE</span></span>
            </p>
          </div>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={!!redirectApresMdp}
        isFirstLogin={true}
        onSuccess={() => {
          authService.setRequirePasswordChange(false);
          const suite = redirectApresMdp;
          setRedirectApresMdp(null);
          if (suite) setRedirectInfo(suite);
        }}
      />

      <RedirectModal
        isOpen={redirectInfo.show}
        role={redirectInfo.role}
        redirectUrl={redirectInfo.redirectUrl}
        userName={redirectInfo.userName}
      />
    </div>
  );
};

export default Login;
